/* eslint-disable @typescript-eslint/no-unused-vars */
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';

import React, {
    useCallback,
    useEffect,
    useImperativeHandle,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';

import {
    JsonEditor,
    StringDisplay,
    toPathString,
    NodeData,
} from 'json-edit-react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import HighlightIcon from '@mui/icons-material/Highlight';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { APIManager, axios } from 'shared/manager';
import { AxiosRequestConfig } from 'axios';
import { downloadFile } from 'shared/helpers/DirectDownload';
import { useHarmonicIntervalFn, useUpdateEffect } from 'react-use';

export type JSONData = Pick<NodeData, 'index' | 'key' | 'level' | 'path'>;

export interface JSONScheme {
    data: object;
    inputHighlighted: Array<Array<string | number>>;
    value: string;
}

export interface inferJSONViewer {
    getData: (scheme: JSONScheme) => Array<JSONData> | undefined;
    getJsonFileDownload: (filename: string) => void;
}

type JSONViewerProps = {
    _className: string;
    canHighlight?: boolean;
    file?: string | null;
    data?: object;
    paths?: Array<Array<string | number>>;
    highlightData?: Array<JSONData>;
    offsetTop?: string;
    onEdit?: (
        index: number,
        key: string | number,
        level: number,
        match: boolean
    ) => void;
    onErased?: (index: number, key: string | number, level: number) => void;
    onLoadSuccess: (data: object) => void;
    onLoadPathSuccessful?: (
        data: object,
        highlightData: Array<JSONData>
    ) => void;
    ref?: React.Ref<inferJSONViewer>;
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const HighlightComponent = (props: any) => (
    <IconButton {...props}>
        <HighlightIcon />
    </IconButton>
);

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const HighlightOffComponent = (props: any) => (
    <IconButton {...props}>
        <HighlightOffIcon />
    </IconButton>
);

export const HighlightCustomComponent: React.FC<any> = (props) => {
    const { setIsEditing, getStyles, nodeData, customNodeProps } = props;
    const styles = getStyles('string', nodeData);
    const typographyRef = useRef<HTMLDivElement | null>(null);

    useLayoutEffect(() => {
        if (customNodeProps) {
            const { mergeReference, finalRender } = customNodeProps;
            if (typeof mergeReference === 'function') {
                mergeReference(nodeData, typographyRef);
            }
            if (typeof finalRender === 'function') {
                finalRender(nodeData);
            }
        }
    }, []);

    return (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div
            onDoubleClick={() => setIsEditing(true)}
            onClick={(e) => {
                if (e.getModifierState('Control') || e.getModifierState('Meta'))
                    setIsEditing(true);
            }}
            className="jer-value-string jer-active"
            ref={typographyRef}
            style={{ ...styles, backgroundColor: 'yellow' }}
        >
            {typeof nodeData.value === 'string' && (
                <StringDisplay
                    {...props}
                    pathString={toPathString(nodeData.path)}
                    styles={styles}
                    value={nodeData.value as string}
                />
            )}
            {typeof nodeData.value === 'number' && (
                <Typography>{nodeData.value}</Typography>
            )}
            {typeof nodeData.value === 'boolean' && (
                <Typography>{String(nodeData.value)}</Typography>
            )}
            {typeof nodeData.value === 'object' && nodeData.value === null && (
                <Typography>{String(nodeData.value)}</Typography>
            )}
        </div>
    );
};

const JSONViewer: React.FC<JSONViewerProps> = React.forwardRef((props, ref) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const {
        file,
        data,
        paths, // path
        onErased,
        onEdit,
        onLoadSuccess,
        onLoadPathSuccessful,
        canHighlight,
        offsetTop,
        highlightData, // save data
        _className,
    } = props;
    const containerViewRef = useRef<HTMLDivElement>(null);

    const [inputHighlighted, setHighlight] = useState<Array<JSONData>>([]);
    const [_data, setData] = useState<any>(data);
    const [isLoading, setLoading] = useState<boolean>(true);
    const containerJSONViewerRef = useRef<HTMLDivElement | null>(null);
    const typographyTargetRef = useRef<HTMLDivElement | null>(null);

    /**
     * Safely gets a nested property from an object using a dot/bracket path.
     * @param {Object} obj - The object to query.
     * @param {string} path - The path string (e.g., "user.address.city" or "items[0].name").
     * @param {*} [defaultValue] - Value to return if the path doesn't exist.
     * @returns {*} - The value at the given path or defaultValue.
     */
    const getByPath = useCallback(
        (obj: object, path: string, defaultValue = undefined) => {
            if (obj == null || typeof path !== 'string') return defaultValue;

            // Convert bracket notation to dot notation: items[0] -> items.0
            const pathArray = path
                .replace(/\[(\w+)\]/g, '.$1')
                .replace(/^\./, '')
                .split('.');

            let current: any = obj;
            let index = 0;
            // eslint-disable-next-line no-restricted-syntax
            for (const key of pathArray) {
                if (
                    current != null &&
                    Object.prototype.hasOwnProperty.call(current, key)
                ) {
                    const array = Object.values(current);
                    current = current[key];
                    // eslint-disable-next-line no-loop-func
                    index = array.findIndex((element) => element === current);
                } else {
                    return defaultValue;
                }
            }
            return index;
        },
        []
    );
    useEffect(() => {
        setData(data);
    }, [data]);
    const [arrayRenderer, setArrayRendering] = useState<Array<NodeData>>([]);
    const [isAutoScroll, setAutoScroll] = useState<boolean>(false);
    const [isSuccessLoading, setSuccessLoading] = useState<boolean>(false);
    const [isSuccessRender, setSuccessRender] = useState<boolean>(false);
    useEffect(() => {
        setLoading(true);
        setSuccessRender(false);
        setArrayRendering([]);
        setSuccessLoading(false);
    }, [file]);
    const handleHarmonicScrolling = useCallback(() => {
        const { current: typo } = typographyTargetRef;

        const bound = typo?.getBoundingClientRect();
        if (bound) {
            const offsetY = offsetTop?.replace('px', '');
            const translateY = parseInt(offsetY ?? '80', 10);
            containerJSONViewerRef.current?.scrollTo(
                bound?.x,
                bound.y - translateY
            );
        }
        setAutoScroll(false);
    }, [offsetTop]);
    useHarmonicIntervalFn(handleHarmonicScrolling, isAutoScroll ? 200 : null);
    useLayoutEffect(() => {
        if (
            arrayRenderer.length === inputHighlighted.length &&
            isSuccessRender === false &&
            isSuccessLoading === true
        ) {
            setAutoScroll(true);

            setSuccessRender(true);
        }
    }, [arrayRenderer]);

    const handleFinalRendering = useCallback((node: NodeData) => {
        setArrayRendering((t) => t.concat([node]));
    }, []);

    useUpdateEffect(() => {
        if (
            paths &&
            Array.isArray(paths) &&
            paths.length > 0 &&
            isLoading === false &&
            isSuccessLoading === false
        ) {
            if (typeof highlightData === 'undefined') {
                // eslint-disable-next-line no-underscore-dangle
                const arrHighlightData = Array.from(paths).map((e) => ({
                    index: getByPath(_data, toPathString(e)) ?? 0,
                    key: e.at(-1) ?? 0,
                    level: e.length,
                    path: e,
                }));
                if (typeof onLoadPathSuccessful === 'function') {
                    onLoadPathSuccessful(_data, arrHighlightData);
                }

                setHighlight(arrHighlightData);
            }
            setSuccessLoading(true);
        }
    }, [paths, isLoading]);
    useLayoutEffect(() => {
        if (highlightData) {
            setHighlight(highlightData);
        }
    }, [highlightData]);
    useLayoutEffect(() => {
        const config: AxiosRequestConfig = {
            responseType: 'arraybuffer',
        };
        APIManager.sendGet(file, false, {}, config, axios)
            .then((res) => {
                const decoder = new TextDecoder('utf-8');
                const jsonString = decoder.decode(res.data);

                const parsedObject = JSON.parse(jsonString);
                setData(parsedObject);
                setLoading(false);
                if (typeof onLoadSuccess === 'function') {
                    onLoadSuccess(parsedObject);
                }
            })
            .catch((error) => {
                console.log('error', error);
            });
    }, [file]);

    useImperativeHandle(
        ref,
        () => ({
            getData: (scheme: JSONScheme) => {
                if (
                    Array.isArray(inputHighlighted) &&
                    inputHighlighted.length === 0
                ) {
                    return Array.from(scheme.inputHighlighted).map((e) => ({
                        index: getByPath(scheme.data, toPathString(e)) ?? 0,
                        key: e.at(-1) ?? 0,
                        level: e.length,
                        path: e,
                    }));
                }
                return inputHighlighted;
            },
            getJsonFileDownload: (fileName: string) => {
                if (typeof file === 'string') downloadFile(file, fileName);
            },
        }),
        [file, getByPath, inputHighlighted]
    );

    const [node, setNode] = useState<NodeData | null>(null);
    const [edit, setEditing] = useState<boolean>(false);
    const [match, setMatch] = useState<boolean>(false);
    useUpdateEffect(() => {
        if (edit) {
            if (typeof onEdit === 'function') {
                if (node && match)
                    onEdit(node.index, node.key, node.level, true);
                if (node && match === false)
                    onEdit(node.index, node.key, node.level, false);
            }
            setEditing(false);
        }
    }, [edit]);

    const handleHighlightButton = useCallback(
        (node: NodeData) => {
            if (
                inputHighlighted.some((t) => {
                    const conditionPath = JSON.stringify(t?.path);
                    const conditionHighlight = JSON.stringify(node.path);
                    return conditionPath === conditionHighlight;
                }) === false
            ) {
                setHighlight((t) => t.concat([node]));
            }
            if (typeof onEdit === 'function') {
                setNode(node);
                setMatch(true);
                setEditing(true);
            }
        },
        [inputHighlighted, onEdit]
    );

    const handleHighlightOffButton = useCallback(
        (node: NodeData) => {
            setHighlight(
                Array.from(inputHighlighted).filter((t) => {
                    const conditionPath = JSON.stringify(t?.path);
                    const conditionHighlight = JSON.stringify(node.path);
                    return conditionPath !== conditionHighlight;
                })
            );
            if (typeof onErased === 'function') {
                onErased(node.index, node.key, node.level);
            }
            if (typeof onEdit === 'function') {
                setNode(node);
                setMatch(false);
                setEditing(true);
            }
        },
        [inputHighlighted, onEdit, onErased]
    );

    return (
        <Box
            className="overlay-json__Viewer viewer"
            ref={containerJSONViewerRef}
        >
            {isLoading ? (
                <Box className="align-loader">
                    <CircularProgress
                        className="mr-2 margin-loader"
                        size={54}
                        color="inherit"
                        title="progress loading"
                    />
                </Box>
            ) : (
                <Box ref={containerViewRef} className="json__data">
                    <JsonEditor
                        className={_className}
                        data={_data}
                        restrictAdd
                        restrictDrag
                        restrictTypeSelection
                        restrictEdit
                        restrictDelete
                        collapse={(obj) => {
                            const arrayPath = Array.from(inputHighlighted)
                                .map((t) =>
                                    Array.from({
                                        length: t.path.length,
                                    })
                                        .map((c, d) => {
                                            const slice = t.path.slice(0, d);
                                            const stringPath =
                                                toPathString(slice);
                                            return stringPath;
                                        })
                                        .filter((d) => d.length > 0)
                                )
                                .flat();

                            const currentPath = toPathString(obj.path);
                            if (
                                obj.level < 3 ||
                                // eslint-disable-next-line array-callback-return

                                arrayPath.some(
                                    (d) =>
                                        currentPath.includes(d) &&
                                        d.split('.').length > 2
                                )
                            ) {
                                return false;
                            }

                            return true;
                        }}
                        icons={{
                            copy: (
                                <IconButton>
                                    <ContentCopyIcon />
                                </IconButton>
                            ),
                        }}
                        onEdit={() => false}
                        customButtons={[
                            {
                                Element: HighlightComponent,
                                onClick: handleHighlightButton,
                            },
                            {
                                Element: HighlightOffComponent,
                                onClick: handleHighlightOffButton,
                            },
                        ]}
                        theme={{
                            styles: {
                                container: { backgroundColor: '#fafafa' },
                            },
                        }}
                        customNodeDefinitions={[
                            {
                                // Condition is a regex to match url strings
                                condition: ({ path }) =>
                                    inputHighlighted.some((t) => {
                                        const conditionPath =
                                            JSON.stringify(path);
                                        const conditionHighlight =
                                            JSON.stringify(t?.path);
                                        return (
                                            conditionPath === conditionHighlight
                                        );
                                    }),

                                element: HighlightCustomComponent,
                                customNodeProps: {
                                    finalRender: handleFinalRendering,

                                    mergeReference: (
                                        node: NodeData,
                                        innerRef: {
                                            current: HTMLDivElement | null;
                                        }
                                    ) => {
                                        if (
                                            inputHighlighted.some((t, i) => {
                                                const conditionPath =
                                                    JSON.stringify(node.path);
                                                const conditionHighlight =
                                                    JSON.stringify(t?.path);
                                                return (
                                                    conditionPath ===
                                                        conditionHighlight &&
                                                    i === 0
                                                );
                                            })
                                        ) {
                                            if (innerRef && innerRef.current) {
                                                typographyTargetRef.current =
                                                    innerRef.current;
                                            }
                                        }
                                    },
                                },
                                showOnView: true,
                                showOnEdit: false,
                            },
                        ]}
                    />
                </Box>
            )}
        </Box>
    );
});

export default JSONViewer;
